<script setup lang="ts">
  import { ref } from 'vue'
  import { useLocale } from '@/composables/useLocale'

  const { t } = useLocale()
  const mobileMenuOpen = ref(false)

  const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value
  }

  const closeMobileMenu = () => {
    mobileMenuOpen.value = false
  }
</script>

<template>
  <header class="site-header">
    <!-- Desktop header -->
    <div class="header-content desktop-header">
      <div class="left-section">
        <nav class="nav-left">
          <router-link :to="{ name: 'Designers' }">{{ t('nav.designers') }}</router-link>
          <router-link :to="{ name: 'Selections' }">{{ t('nav.selections') }}</router-link>
          <router-link :to="{ name: 'Blog' }">{{ t('nav.blog') }}</router-link>
          <router-link :to="{ name: 'Store' }">{{ t('nav.store') }}</router-link>
        </nav>
      </div>

      <div class="castle-wrapper">
        <img src="@/assets/graphics/castle.svg" alt="castle" class="castle-img" />
      </div>

      <div class="right-section">
        <nav class="nav-right">
          <a href="#">{{ t('nav.about') }}</a>
          <router-link class="cart" :to="{ name: 'Basket' }">{{ t('nav.basket') }}</router-link>
        </nav>
      </div>
    </div>

    <!-- Mobile header -->
    <div class="header-content mobile-header">
      <div class="mobile-nav-stick mobile-nav-left">
        <button class="mobile-nav-btn" @click="toggleMobileMenu">
          {{ t('nav.menu') }}
        </button>
      </div>

      <div class="castle-wrapper-mobile">
        <router-link :to="{ name: 'Home' }" @click="closeMobileMenu">
          <img src="@/assets/graphics/castle.svg" alt="castle" class="castle-img-mobile" />
        </router-link>
      </div>

      <div class="mobile-nav-stick mobile-nav-right">
        <router-link
          class="mobile-nav-btn"
          :to="{ name: 'Basket' }"
        >
          {{ t('nav.basket').toUpperCase() }}
        </router-link>
      </div>
    </div>

    <!-- Mobile menu overlay -->
    <Transition name="menu-fade">
      <div v-if="mobileMenuOpen" class="mobile-menu-overlay" @click.self="closeMobileMenu">
        <nav class="mobile-menu-nav">
          <router-link :to="{ name: 'Designers' }" @click="closeMobileMenu">{{ t('nav.designers') }}</router-link>
          <router-link :to="{ name: 'Selections' }" @click="closeMobileMenu">{{ t('nav.selections') }}</router-link>
          <router-link :to="{ name: 'Blog' }" @click="closeMobileMenu">{{ t('nav.blog') }}</router-link>
          <router-link :to="{ name: 'Store' }" @click="closeMobileMenu">{{ t('nav.store') }}</router-link>
          <a href="#" @click="closeMobileMenu">{{ t('nav.about') }}</a>
        </nav>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 30px 24px;
  background: transparent;
}

.header-content {
  gap: 24px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

/* Desktop header */
.desktop-header {
  display: flex;
}

.mobile-header {
  display: none;
}

.left-section,
.right-section {
  flex: 1;
  display: flex;
  align-items: flex-start;
}

.left-section {
  display: flex;
  justify-content: flex-start;
}

.nav-left,
.nav-right {
  display: flex;
  align-items: center;
  background: #000;
  height: 20px;
  padding: 0 20px;
}

.nav-left {
  justify-content: flex-start;
  gap: 20px;
  flex-grow: 1;
  margin-left: 120px;
}

.right-section {
  justify-content: flex-end;
}

.nav-right {
  justify-content: flex-end;
  gap: 20px;
  width: 100%;
}

.nav-left a,
.nav-right a {
  text-decoration: none;
  color: #fff;
  text-transform: uppercase;
  font-size: 14px;
  white-space: nowrap;
  font-family: 'Roboto Mono', monospace;
  letter-spacing: -0.56px;
}

.castle-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

.castle-img {
  height: 60px;
  position: relative;
  top: -20px;
}

/* Mobile header styles */
.mobile-header {
  display: none;
}

.mobile-nav-stick {
  flex: 1;
  display: flex;
  background: #000;
  height: 20px;
  padding: 0;
}

.mobile-nav-stick.mobile-nav-left {
  justify-content: flex-start;
}

.mobile-nav-stick.mobile-nav-right {
  justify-content: flex-end;
}

.mobile-nav-btn {
  display: flex;
  align-items: center;
  background: transparent;
  color: #fff;
  border: none;
  height: 20px;
  padding: 0 20px;
  font-family: 'Roboto Mono', monospace;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: -0.56px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.castle-wrapper-mobile {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: -15px;
  z-index: 1;
  flex-shrink: 0;
}

.castle-img-mobile {
  height: 48px;
}

/* Mobile menu overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.mobile-menu-nav a {
  text-decoration: none;
  color: #fff;
  font-family: 'Arial Narrow', sans-serif;
  font-size: 40px;
  text-transform: uppercase;
  letter-spacing: -0.4px;
  line-height: 1.2;
}

/* Menu transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.25s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .nav-left,
  .nav-right {
    gap: 15px;
    padding: 0 15px;
  }

  .left-section {
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .site-header {
    padding: 0;
    height: auto;
    top: 0;
  }

  .desktop-header {
    display: none;
  }

  .mobile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    position: relative;
    padding: 0;
    width: 100%;
    min-height: 20px;
    top: 20px;
  }

  .mobile-nav-stick {
    flex: 1 1 ;
    min-width: 0;
  }

  .mobile-nav-stick.mobile-nav-left {
    margin-left: 0;
    justify-content: flex-start;
  }

  .mobile-nav-stick.mobile-nav-right {
    margin-right: 0;
    justify-content: flex-end;
  }

  .castle-wrapper-mobile {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -14px;
    z-index: 1;
    flex-shrink: 0;
    pointer-events: none;
  }

  .castle-wrapper-mobile a {
    pointer-events: auto;
  }
}
</style>
